import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';

// Supabase 配置类型
export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

// 从环境变量获取配置
const getConfig = (): SupabaseConfig => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'Supabase configuration missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file'
    );
  }

  return { url, anonKey };
};

// Supabase 客户端单例
let supabaseClient: SupabaseClient | null = null;

// 获取 Supabase 客户端
export const getSupabaseClient = (): SupabaseClient => {
  if (!supabaseClient) {
    const config = getConfig();
    supabaseClient = createClient(config.url, config.anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    });
  }
  return supabaseClient;
};

// 检查 Supabase 是否已配置
export const isSupabaseConfigured = (): boolean => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  return !!(url && anonKey && url !== 'your_supabase_url_here');
};

// 生成简单UUID（兼容模式）
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// 匿名登录
export const signInAnonymously = async (): Promise<{ userId: string | null; error: Error | null }> => {
  try {
    const supabase = getSupabaseClient();
    
    // 检查是否已有会话
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user) {
      return { userId: session.user.id, error: null };
    }

    // 尝试匿名登录
    const { data, error } = await supabase.auth.signInAnonymously();
    
    if (error) {
      // 如果匿名登录失败，尝试生成一个本地用户ID
      const localUserId = localStorage.getItem('health_monitor_local_user_id');
      if (localUserId) {
        return { userId: localUserId, error: null };
      }
      
      const newLocalUserId = `local_${generateUUID()}`;
      localStorage.setItem('health_monitor_local_user_id', newLocalUserId);
      return { userId: newLocalUserId, error: null };
    }

    return { userId: data.user?.id || null, error: null };
  } catch (err) {
    // const error = err instanceof Error ? err : new Error(String(err));
    
    // 降级到本地用户ID
    const localUserId = localStorage.getItem('health_monitor_local_user_id');
    if (localUserId) {
      return { userId: localUserId, error: null };
    }
    
    const newLocalUserId = `local_${generateUUID()}`;
    localStorage.setItem('health_monitor_local_user_id', newLocalUserId);
    return { userId: newLocalUserId, error: null };
  }
};

// 邮箱登录
export const signInWithEmail = async (email: string, password: string) => {
  const supabase = getSupabaseClient();
  return supabase.auth.signInWithPassword({ email, password });
};

// 邮箱注册
export const signUpWithEmail = async (email: string, password: string) => {
  const supabase = getSupabaseClient();
  return supabase.auth.signUp({ email, password });
};

// 登出
export const signOut = async () => {
  const supabase = getSupabaseClient();
  return supabase.auth.signOut();
};

// 获取当前用户
export const getCurrentUser = async () => {
  const supabase = getSupabaseClient();
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    // 检查本地用户ID
    const localUserId = localStorage.getItem('health_monitor_local_user_id');
    if (localUserId) {
      return { id: localUserId, isAnonymous: true, isLocal: true };
    }
    return null;
  }
  
  return { ...user, isAnonymous: user.is_anonymous || false, isLocal: false };
};

// 数据库类型定义
export interface SyncHealthRecord {
  id: number;
  user_id: string;
  date: string;
  type: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  device_id?: string;
  is_deleted: boolean;
}

export interface SyncBloodPressureRecord {
  id: number;
  record_id: number;
  user_id: string;
  date: string;
  systolic: number;
  diastolic: number;
  heart_rate?: number;
  measure_time?: string;
  created_at: string;
  updated_at: string;
}

export interface SyncBloodSugarRecord {
  id: number;
  record_id: number;
  user_id: string;
  date: string;
  value: number;
  type: string;
  created_at: string;
  updated_at: string;
}

// 订阅实时变更
export const subscribeToChanges = (
  userId: string,
  onRecordsChange: (payload: any) => void,
  onBPChange: (payload: any) => void,
  onBSChange: (payload: any) => void
): RealtimeChannel => {
  const supabase = getSupabaseClient();
  
  const channel = supabase
    .channel('health_sync')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'health_records_sync',
        filter: `user_id=eq.${userId}`
      },
      onRecordsChange
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'blood_pressure_sync',
        filter: `user_id=eq.${userId}`
      },
      onBPChange
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'blood_sugar_sync',
        filter: `user_id=eq.${userId}`
      },
      onBSChange
    )
    .subscribe();

  return channel;
};

// 取消订阅
export const unsubscribeFromChanges = (channel: RealtimeChannel) => {
  channel.unsubscribe();
};
